// The country settings page's rules (slice 4.4), apart from the page so they run
// on Node in the admin tests (tests/admin/test_country_settings_model.py).
//
// The API is the authority: every value is judged there by the same gate the
// campaign runner refuses a start on. What is here is the quick half — the
// sentence under a field while it is being typed, the fields that changed, and
// the API's refusal put back on the fields it names.

import { ApiError } from "@/lib/api";
import type {
  SendingAccount,
  SendingAccountChange,
  SendingAccountEvent,
  SendingAccountValues,
  SettingsCountry,
} from "@/lib/api";

export type FieldKind = "text" | "email" | "host" | "url" | "hour" | "count" | "ses" | "pool";

export interface FieldSpec {
  name: string;
  label: string;
  kind: FieldKind;
  /** Needed before the account may be active (the gate's rule 5 fields). */
  required: boolean;
  hint?: string;
  placeholder?: string;
  /** The column's width, as the API refuses past it. */
  max?: number;
}

export interface Section {
  id: string;
  title: string;
  description: string;
  fields: FieldSpec[];
}

export const SECTIONS: Section[] = [
  {
    id: "sender",
    title: "Feladó",
    description: "Ezen a néven és erről a címről kapja a levelet a címzett, és ide válaszol.",
    fields: [
      { name: "label", label: "Megnevezés", kind: "text", required: false, max: 80, hint: "Csak itt látszik." },
      { name: "sender_name", label: "Feladó neve", kind: "text", required: true, max: 120, placeholder: "MobilFox" },
      {
        name: "sender_email",
        label: "Feladó címe",
        kind: "email",
        required: true,
        max: 320,
        placeholder: "hirlevel@mobilfox.com",
        hint: "Ellenőrzött küldő domainen kell legyen.",
      },
      { name: "reply_to", label: "Válaszcím", kind: "email", required: true, max: 320 },
    ],
  },
  {
    id: "company",
    title: "Cég és jogi adatok",
    description: "A kötelező lábléc: minden levél alján ez áll, kampányból nem írható felül.",
    fields: [
      { name: "company_name", label: "Cégnév", kind: "text", required: true, max: 160 },
      { name: "company_address", label: "Székhely", kind: "text", required: true, max: 320 },
      {
        name: "tax_number",
        label: "Adószám",
        kind: "text",
        required: false,
        max: 64,
        hint: "Az adószám és a cégjegyzékszám közül legalább az egyik kell.",
      },
      { name: "registration_number", label: "Cégjegyzékszám", kind: "text", required: false, max: 64 },
      { name: "phone", label: "Telefonszám", kind: "text", required: false, max: 64 },
      {
        name: "website_url",
        label: "Weboldal",
        kind: "url",
        required: false,
        max: 500,
        placeholder: "https://www.mobilfox.hu",
        hint: "A láblécben linkként jelenik meg.",
      },
    ],
  },
  {
    id: "unsubscribe",
    title: "Leiratkozás",
    description: "Az egykattintásos leiratkozó link szövege és a beállítások linkje, a fiók nyelvén.",
    fields: [
      {
        name: "unsubscribe_text",
        label: "Leiratkozó szöveg",
        kind: "text",
        required: true,
        max: 500,
        hint: "A jogilag átnézett mondat, a fiók nyelvén.",
      },
      {
        name: "preference_center_url",
        label: "Beállítások linkje",
        kind: "url",
        required: false,
        max: 500,
        placeholder: "https://mobilfox.hu/beallitasok",
        hint: "Ha üres, a láblécben nincs beállítások-link.",
      },
      { name: "language", label: "Nyelv", kind: "text", required: true, max: 8, placeholder: "hu" },
    ],
  },
  {
    id: "sending",
    title: "Domainek és küldés",
    description:
      "A követő domainre mutat a nyitás-pixel, minden link és a leiratkozás; a pool és az SES-fiók dönti el, honnan megy ki a levél.",
    fields: [
      { name: "sending_domain", label: "Küldő domain", kind: "host", required: true, max: 255 },
      {
        name: "tracking_domain",
        label: "Követő domain",
        kind: "host",
        required: true,
        max: 255,
        hint: "Csak olyan host, amelyet kiszolgálunk (EMC_TRACKING_DOMAINS).",
      },
      { name: "ses_account", label: "SES-fiók", kind: "ses", required: true },
      { name: "ip_pool_code", label: "IP-pool", kind: "pool", required: true },
      { name: "timezone", label: "Időzóna", kind: "text", required: true, max: 64, placeholder: "Europe/Budapest" },
      { name: "send_window_start_hour", label: "Küldési ablak kezdete (óra)", kind: "hour", required: true },
      { name: "send_window_end_hour", label: "Küldési ablak vége (óra)", kind: "hour", required: true },
      {
        name: "frequency_cap_messages",
        label: "Gyakorisági korlát (levél)",
        kind: "count",
        required: false,
        hint: "0: nincs korlát.",
      },
      { name: "frequency_cap_days", label: "Gyakorisági korlát (nap)", kind: "count", required: false },
    ],
  },
];

export const FIELDS: FieldSpec[] = SECTIONS.flatMap((section) => section.fields);
const SPEC = new Map(FIELDS.map((field) => [field.name, field]));
/** Everything the form sends, the active switch included. */
export const FIELD_NAMES: string[] = [...FIELDS.map((field) => field.name), "is_active"];
/** Locked while a campaign is running from the account. */
export const ROUTING_FIELDS = ["ses_account", "ip_pool_code"];

export type FormValues = Record<string, string | number | boolean>;

/** A saved account as the form shows it. */
export function formOf(account: SendingAccount): FormValues {
  const source = account as unknown as Record<string, string | number | boolean>;
  const out: FormValues = {};
  for (const name of FIELD_NAMES) out[name] = source[name] ?? "";
  return out;
}

/** A new account's form: empty, with the country's language and time zone,
 * the default sending window, inactive. */
export function blankForm(country: SettingsCountry): FormValues {
  const out: FormValues = {};
  for (const field of FIELDS) out[field.name] = "";
  return {
    ...out,
    label: `MobilFox ${country.code}`,
    language: country.language,
    timezone: country.timezone,
    send_window_start_hour: 8,
    send_window_end_hour: 20,
    frequency_cap_messages: 0,
    frequency_cap_days: 7,
    is_active: false,
  };
}

/** A value as the API will store it: text trimmed, hosts in lower case, the
 * SES account and the pool in upper case, numbers as numbers. */
export function normalised(name: string, value: string | number | boolean): string | number | boolean {
  if (name === "is_active") return Boolean(value);
  const kind = SPEC.get(name)?.kind;
  if (kind === "hour" || kind === "count") {
    const text = String(value).trim();
    return /^\d+$/.test(text) ? Number(text) : text;
  }
  const text = String(value).trim();
  if (kind === "host") return text.toLowerCase();
  if (kind === "ses" || kind === "pool") return text.toUpperCase();
  return text;
}

/** The fields that differ from what was loaded, and what was loaded for each
 * of them — the API refuses a save whose `expected` has moved meanwhile. */
export function changesOf(
  before: FormValues,
  after: FormValues,
): { changes: SendingAccountValues; expected: SendingAccountValues } {
  const changes: SendingAccountValues = {};
  const expected: SendingAccountValues = {};
  for (const name of FIELD_NAMES) {
    const was = normalised(name, before[name] ?? "");
    const now = normalised(name, after[name] ?? "");
    if (was !== now) {
      changes[name] = now;
      expected[name] = was;
    }
  }
  return { changes, expected };
}

/** Every field, as the API's create takes it. */
export function valuesOf(form: FormValues): SendingAccountValues {
  const out: SendingAccountValues = {};
  for (const name of FIELD_NAMES) out[name] = normalised(name, form[name] ?? "");
  return out;
}

export interface CheckContext {
  verified: string[];
  tracking: string[];
  sesAccounts: string[];
  pools: string[];
}

const EMAIL = /^[^\s@<>,;"]+@([^\s@<>,;"]+)$/;
const HOST = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/;

/** The verified domain that covers `domain`, the way an SES identity covers
 * its subdomains: `mobilfox.com` covers `hu.mobilfox.com`. */
export function verifiedParent(domain: string, verified: string[]): string | null {
  const name = domain.trim().toLowerCase().replace(/\.$/, "");
  return verified.find((parent) => name === parent || name.endsWith(`.${parent}`)) ?? null;
}

function listOr(values: string[]): string {
  return values.length > 0 ? values.join(", ") : "egy sincs megadva";
}

/** The sentence under each field that is wrong, in Hungarian. `active`: the
 * account is (or is about to be) active, so an empty required field is a
 * mistake rather than a draft. Only what can be said without the server; the
 * server says the rest when the form is saved. */
export function checkForm(values: FormValues, context: CheckContext, active: boolean): Record<string, string> {
  const errors: Record<string, string> = {};
  const text = (name: string) => String(values[name] ?? "").trim();

  for (const field of FIELDS) {
    const value = text(field.name);
    if (value === "") {
      if (active && field.required) errors[field.name] = "Kötelező, ha a fiók aktív.";
      continue;
    }
    if (field.max !== undefined && value.length > field.max) {
      errors[field.name] = `Legfeljebb ${field.max} karakter.`;
      continue;
    }
    if (field.kind === "email") {
      const domain = (EMAIL.exec(value)?.[1] ?? "").toLowerCase();
      if (!HOST.test(domain)) {
        errors[field.name] = "Egyetlen, puszta email-cím kell (név és <> nélkül).";
      } else if (field.name === "sender_email" && verifiedParent(domain, context.verified) === null) {
        errors[field.name] = `A cím domainje nincs az ellenőrzött küldő domainek között: ${listOr(context.verified)}.`;
      }
    } else if (field.kind === "host") {
      const host = value.toLowerCase();
      if (!HOST.test(host)) {
        errors[field.name] = "Puszta hostnév kell, séma és perjel nélkül (pl. link.mobilfox.com).";
      } else if (field.name === "sending_domain" && verifiedParent(host, context.verified) === null) {
        errors[field.name] = `Nincs ellenőrzött domain alatt: ${listOr(context.verified)}.`;
      } else if (field.name === "tracking_domain" && !context.tracking.includes(host)) {
        errors[field.name] = `Ezt a hostot nem szolgáljuk ki. Lehet: ${listOr(context.tracking)}.`;
      }
    } else if (field.kind === "url") {
      if (!/^https:\/\/[^\s/]+\.[^\s/]+/i.test(value)) {
        errors[field.name] = "https:// kezdetű, teljes link kell.";
      }
    } else if (field.kind === "hour") {
      if (!/^\d+$/.test(value) || Number(value) > 23) errors[field.name] = "Egész óra 0 és 23 között.";
    } else if (field.kind === "count") {
      if (!/^\d+$/.test(value)) errors[field.name] = "Nemnegatív egész szám.";
    } else if (field.kind === "ses") {
      if (!context.sesAccounts.includes(value.toUpperCase())) {
        errors[field.name] = `Ismeretlen SES-fiók. Lehet: ${listOr(context.sesAccounts)}.`;
      }
    } else if (field.kind === "pool") {
      if (!context.pools.includes(value.toUpperCase())) errors[field.name] = "Válassz egy meglévő poolt.";
    }
  }
  if (
    !errors.send_window_end_hour &&
    text("send_window_start_hour") !== "" &&
    text("send_window_start_hour") === text("send_window_end_hour")
  ) {
    errors.send_window_end_hour = "A kezdet és a vég nem lehet ugyanaz az óra.";
  }
  if (active && text("tax_number") === "" && text("registration_number") === "") {
    errors.tax_number = "Az adószám és a cégjegyzékszám közül legalább az egyik kell.";
  }
  return errors;
}

export interface Refusal {
  message: string;
  fields: Record<string, string>;
}

/** The API's refusal, on the fields it names. Two shapes arrive: ours
 * (`{message, errors: [{field, message}]}`) and FastAPI's own validation list
 * (`[{loc: [..., field], msg}]`). Anything else is a sentence and no field. */
export function refusalOf(error: unknown): Refusal {
  const message = error instanceof Error ? error.message : "Ismeretlen hiba.";
  const fields: Record<string, string> = {};
  const detail = error instanceof ApiError ? error.detail : null;
  if (detail !== null && typeof detail === "object" && !Array.isArray(detail)) {
    const errors = (detail as { errors?: unknown }).errors;
    if (Array.isArray(errors)) {
      for (const item of errors) {
        const { field, message: said } = item as { field?: unknown; message?: unknown };
        if (typeof field === "string" && typeof said === "string" && !(field in fields)) {
          fields[field] = said;
        }
      }
    }
  } else if (Array.isArray(detail)) {
    for (const item of detail) {
      const { loc, msg } = (item ?? {}) as { loc?: unknown; msg?: unknown };
      const field = Array.isArray(loc) ? loc[loc.length - 1] : null;
      if (typeof field === "string" && SPEC.has(field) && typeof msg === "string" && !(field in fields)) {
        fields[field] = msg;
      }
    }
  }
  return { message, fields };
}

function shown(value: string | number | boolean | null): string {
  if (value === null || value === "") return "—";
  if (typeof value === "boolean") return value ? "aktív" : "inaktív";
  return `„${value}”`;
}

/** One change of the log, as a line: `A székhely: „régi” → „új”`. */
export function changeLine(change: SendingAccountChange): string {
  if (change.old === null) return `${change.label}: ${shown(change.new)}`;
  return `${change.label}: ${shown(change.old)} → ${shown(change.new)}`;
}

/** What a save was, in a word. */
export function eventTitle(event: SendingAccountEvent): string {
  if (event.action === "account.created") return "Létrehozva";
  // Values a seed or a migration set, written down before the first edit
  // after them: nobody switched anything on or off here.
  if (event.action === "account.recorded") return "Az oldalon kívül beállítva";
  if (event.from_state !== event.to_state) return event.to_state === "active" ? "Aktiválva" : "Inaktiválva";
  return "Módosítva";
}

/** A new account's create omits what is still empty, so a draft's numbers
 * and selects that were left alone do not arrive as "". */
export function createValues(form: FormValues): SendingAccountValues {
  const out: SendingAccountValues = {};
  for (const [name, value] of Object.entries(valuesOf(form))) {
    if (value !== "") out[name] = value;
  }
  return out;
}
